import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Upload, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../utils/apiClient';
import { Button } from '../ui/button';

// Uploads-tab twin of the Bank Accounts "Fill gap" chip: lists every open gap reminder (deleted statement,
// start-date move-back or provider history limit) with a direct Upload action. Same data source, no second gap mechanism.
const reasonCopy = (r) => {
  if (r.reason === 'provider_history_limit') return ` — your bank connection could not pull history back to ${r.gap_start}`;
  if (r.reason === 'start_date_moved') return ' — needed since your start date moved back';
  return ` — left by removing "${r.deleted_filename || 'a statement'}"`;
};

const OpenedLaterInline = ({ reminder, onDone }) => {
  const [show, setShow] = useState(false);
  const [openedOn, setOpenedOn] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!openedOn) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post('/bank-feeds/gap-fill/account-opened-later', { bank_account_id: reminder.bank_account_id, opened_on: openedOn });
      if (data?.ok) { toast.success('Noted — opening balance set to 0 and this gap is closed.'); onDone?.(); }
      else toast.error(data?.reason || 'Could not record the opening date');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the opening date'); }
    finally { setBusy(false); }
  };
  if (!show) {
    return (
      <button type="button" onClick={() => setShow(true)}
        className="self-start text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
        data-testid={`gap-reminder-opened-later-${reminder.bank_account_id}`}>
        Was this account only opened after {reminder.gap_start}? Tell us the opening date instead
      </button>
    );
  }
  return (
    <div className="rounded-md border border-amber-300 bg-white/70 p-2.5 space-y-2" data-testid={`gap-reminder-opened-later-${reminder.bank_account_id}`}>
      <p className="text-xs text-amber-900">If the account did not exist on {reminder.gap_start}, no statement is needed: its opening balance becomes 0 and this reminder closes.</p>
      <label className="block text-xs font-medium text-amber-900" htmlFor={`opened-on-${reminder.bank_account_id}`}>Date the account was opened</label>
      <div className="relative">
        <CalendarCheck className="w-4 h-4 text-amber-700 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        {!openedOn && <span className="absolute left-8 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">Tap to pick a date</span>}
        <input id={`opened-on-${reminder.bank_account_id}`} type="date" value={openedOn} min={reminder.gap_start} onChange={(e) => setOpenedOn(e.target.value)}
          className={`w-full min-h-[38px] border border-amber-300 rounded pl-8 pr-2 py-1.5 text-sm bg-white text-slate-900 ${openedOn ? '' : '[&::-webkit-datetime-edit]:text-transparent'}`}
          data-testid={`gap-reminder-opened-on-${reminder.bank_account_id}`} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="bg-[#108981] hover:bg-[#0d6f69] text-white" disabled={!openedOn || busy} onClick={submit}
          data-testid={`gap-reminder-opened-later-btn-${reminder.bank_account_id}`}>
          {openedOn ? `Confirm — opened on ${openedOn}` : 'Confirm opening date'}
        </Button>
        <Button size="sm" variant="ghost" className="text-amber-900" onClick={() => { setShow(false); setOpenedOn(''); }}
          data-testid={`gap-reminder-opened-later-cancel-${reminder.bank_account_id}`}>Cancel</Button>
      </div>
    </div>
  );
};

export const GapRemindersStrip = ({ bankAccounts = [], onUpload, refreshKey }) => {
  const [reminders, setReminders] = useState([]);
  const refetch = useCallback(() => apiClient.get('/bank-feeds/pending-gap-reminders')
    .then(({ data }) => setReminders(data?.reminders || []))
    .catch(() => {}), []);
  useEffect(() => { refetch(); }, [refetch, refreshKey]);
  if (!reminders.length) return null;
  const name = (id) => { const a = bankAccounts.find((x) => x.id === id); return a ? `${a.bank_name || 'Bank'} · ${a.account_name || ''}` : 'Bank account'; };
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="gap-reminders-strip">
      <p className="text-sm font-medium text-amber-900 flex items-center gap-2"><AlertCircle className="w-4 h-4" />Statements needed</p>
      {reminders.map((r) => (
        <div key={r.id} className="flex flex-col gap-1.5 text-sm text-amber-900" data-testid={`gap-reminder-${r.bank_account_id}`}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="flex-1">
              <strong>{name(r.bank_account_id)}</strong>: {r.gap_start} → {r.gap_end}
              <span data-testid={`gap-reminder-reason-${r.bank_account_id}`}>{reasonCopy(r)}</span>
              {r.partial && r.covered_through && (
                <span data-testid={`gap-reminder-partial-${r.bank_account_id}`}> · your statement covered up to {r.covered_through}; the balance does not join the books on {r.join_check?.next_row_date}, so this window is still missing</span>
              )}
              {(r.reason === 'start_date_moved' || r.reason === 'provider_history_limit') && r.feed_check && ' · fill from a bank statement (the bank feed is not used for this gap)'}
            </span>
            <Button size="sm" variant="outline" className="border-amber-400" onClick={() => onUpload?.(r.bank_account_id)} data-testid={`gap-reminder-upload-${r.bank_account_id}`}>
              <Upload className="w-3.5 h-3.5 mr-1" />Upload statement
            </Button>
          </div>
          {(r.reason === 'start_date_moved' || r.reason === 'provider_history_limit') && !r.partial && (
            <OpenedLaterInline reminder={r} onDone={refetch} />
          )}
        </div>
      ))}
    </div>
  );
};
